/**
 * Security track: proof of work for guest sign-up, registration and mail-triggering calls.
 * The server (apps/api/src/services/abuse.ts) hands out a signed challenge; the browser finds a nonce so that
 * sha256(`${challenge}:${nonce}`) starts with `bits` zero bits and sends `challenge~nonce` in the X-PoW header.
 * This costs a person about a second once, and costs a bot farm real CPU time per account.
 */

interface PowChallenge { required: boolean; challenge?: string; bits?: number }

const WORKER_SRC = `
self.onmessage = async (e) => {
  const { challenge, bits } = e.data;
  const enc = new TextEncoder();
  const lz = (b) => { let n = 0; for (const x of b) { if (x === 0) { n += 8; continue; } n += Math.clz32(x) - 24; break; } return n; };
  for (let i = 0; ; i++) {
    const nonce = i.toString(36);
    const d = new Uint8Array(await crypto.subtle.digest('SHA-256', enc.encode(challenge + ':' + nonce)));
    if (lz(d) >= bits) { self.postMessage(challenge + '~' + nonce); return; }
  }
};`;

function solve(challenge: string, bits: number): Promise<string> {
  const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: 'text/javascript' }));
  const worker = new Worker(url);
  return new Promise<string>((resolve, reject) => {
    worker.onmessage = (e: MessageEvent<string>) => { worker.terminate(); URL.revokeObjectURL(url); resolve(e.data); };
    worker.onerror = (e) => { worker.terminate(); URL.revokeObjectURL(url); reject(new Error(e.message)); };
    worker.postMessage({ challenge, bits });
  });
}

/** Headers to add to a protected call: empty when the server does not ask for a proof. */
export async function powHeaders(): Promise<Record<string, string>> {
  const res = await fetch('/api/auth/pow', { credentials: 'same-origin' });
  if (!res.ok) return {};
  const c = await res.json() as PowChallenge;
  if (!c.required || !c.challenge || !c.bits) return {};
  return { 'X-PoW': await solve(c.challenge, c.bits) };
}
